<template>
  <VcWidget class="product-info" size="lg">
    <div class="product-info__container">
      <div class="product-info__side">
        <ImageGallery :images="product.images" />

        <ProductVideos class="product-info__videos" :videos="product.videos" />

        <InnoAddToCompareProduct :product="product" class="mt-8 inline-flex" />
      </div>

      <div class="product-info__print">
        <VcImage :src="product.imgSrc" class="product-info__print-img" />
      </div>

      <div class="product-info__content">
        <div v-if="model?.blocks?.length" class="product-info__blocks">
          <template v-for="(block, index) in model.blocks">
            <component
              :is="block.type"
              v-if="block.type !== 'product-variations'"
              :key="block.id || index"
              :model="block"
              :product="product"
            />
          </template>

          <div class="flex self-end">
            <VcIcon
              name="veld-icon-bg-questionmark"
              class="mb-0.5 mr-2 text-[color:var(--color-secondary-500)]"
              size="md"
            />

            <div class="font-bold text-[color:var(--color-secondary-500)]">
              <span v-html-safe="$t('custom.product_details.faq')"></span>
            </div>
          </div>
        </div>

        <div v-if="product.isConfigurable" class="product-info__config">
          <VcButton
            color="secondary"
            variant="outline"
            prepend-icon="cube-transparent"
            @click="handleCreateConfiguration"
          >
            {{ $t("shared.catalog.product_details.create_configuration_button") }}
          </VcButton>
        </div>
      </div>
    </div>
    <ProductDownloads :assets="product.assets" :download-url="downloadUrlProperty" />
  </VcWidget>
</template>

<script setup lang="ts">
import { ImageGallery, InnoDiscountBadge, ProductVideos, ProductDownloads } from "@/shared/catalog";
import { InnoAddToCompareProduct } from "@/shared/compare";
import type { IPageContent } from "../../types";
import type { Product } from "@/core/api/graphql/types";
import { computed } from "vue";

interface IProps {
  product: Product;
  model: IPageContent;
}

const props = defineProps<IProps>();

const downloadUrlProperty = computed<string | undefined>(() => {
  const value = props.product?.properties?.find((item) => item.name === "productpagina_url")?.value;

  return value !== undefined && value !== null ? String(value) : "";
});

function handleCreateConfiguration() {
  const productConfigurationElement = document.getElementById("product-configuration-anchor");
  if (productConfigurationElement) {
    productConfigurationElement.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" });
  }
}
</script>

<style lang="scss">
.product-info {
  &__container {
    @apply flex flex-col lg:flex-row lg:gap-8 print:flex-row print:gap-4;
  }

  &__side {
    @apply mb-4 flex-none print:hidden lg:mb-0 lg:w-1/3;
  }

  &__videos {
    @apply mt-8 lg:mt-3;
  }

  &__print {
    @apply hidden aspect-square w-40 flex-none print:block;
  }

  &__print-img {
    @apply w-full rounded border;
  }

  &__content {
    @apply mt-5 lg:mt-0 lg:grow print:mt-5 print:grow;
  }

  &__blocks {
    @apply flex flex-col justify-between gap-5 print:w-2/3 lg:grow h-full;
  }
}
</style>
